import {
  CalendarOutlined,
  CreditCardOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Drawer,
  Flex,
  Form,
  Input,
  List,
  Popconfirm,
  Radio,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import {
  approveOrder,
  estimateOrder,
  getOrderDetailByOrderId,
  isRideServiceAvailable,
  updateOrderState,
} from 'api/orders';
import FormItem from 'components/FormItem';
import JumpLink from 'components/JumpLink';
import withGuard from 'components/withGuard';
import { AdminContext } from 'constants/contexts';
import { PERMISSIONS } from 'constants/permissions';
import Header from 'components/OrderOverlay/Header';
import {
  getItemValue,
  isOmniPartner,
  isFoodHubSelfDelivery,
} from 'utils/order';
import dayjs from 'dayjs';
import { useHasAnyOfPermissions } from 'hooks/useHasPermission';
import useIsDesktop from 'hooks/useIsDesktop';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { captureException } from 'utils/errors';
import http, { getHttpErrorMessage } from 'utils/http';
import { renderServingSizeLabel } from 'utils/orders';
import TimeDialogue from 'containers/Order/components/TimeDialogue';
import { Check } from '@mui/icons-material';
import { checkExistDispatcher, createDispatcherJob } from './requests';
import ChangeLog from './ChangeLog';
import { PaymentTypes, RIDE_STATUS, WL_CONFIG } from '../../constants';
import { formatAmount, getData } from '../../utils';
import * as orderStatus from '../../constants/status';
import Row from './Row';

function OrderOverlay({
  orderId: orderIdParam,
  data,
  handleOrderUpdate,
  onClose: handleClose,
  disableActions,
}) {
  const { orderId = orderIdParam } = useParams();
  const [orderDetail, setOrderDetail] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const order = useMemo(
    () => ({
      ...data,
      ...(+orderDetail.id === +orderId ? orderDetail : {}),
    }),
    [data, orderDetail, orderId],
  );
  const [timeEST, setTimeEST] = useState('');
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [timeConfirmLoading, setTimeConfirmLoading] = useState(false);
  const [orderTime, setOrderTime] = useState(order.estimate);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [readyLoading, setReadyLoading] = useState(false);
  const [deliverLoading, setDeliverLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [selectedEstimateOption, setSelectedEstimateOption] = useState();
  const [estimatedPickupTs, setEstimatedPickupTs] = useState(null);
  const [canRequestDispatcher, setCanRequestDispatcher] = useState(false);
  const isAdmin = useHasAnyOfPermissions([PERMISSIONS.ADMIN_ACCESS]);
  const { organizationId } = useContext(AdminContext);

  const isScheduled = Boolean(order.scheduled_for);
  const isFoodHubSelfDeliveryOrder = isFoodHubSelfDelivery(order);
  const isFoodHubSelfDeliveryOrOmniPartner =
    isFoodHubSelfDeliveryOrder || isOmniPartner(order);
  const { message } = App.useApp();
  const open = Boolean(orderId);

  useEffect(() => {
    const prevValue = document.body.style.overflow;
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = prevValue;
    };
  }, [open]);

  const handleEditOrder = (id) => {
    navigate(`/console/orders/${id}/edit`);
  };

  function onClose() {
    handleClose ? handleClose() : navigate('..');
  }
  const estimateOptions = useMemo(
    () => [
      { id: 0, title: '15 minutes', time: 15 },
      { id: 1, title: '25 minutes', time: 25 },
      { id: 2, title: '45 minutes', time: 45 },
      {
        id: -1,
        title: <CustomEstInput value={timeEST} onChange={setTimeEST} />,
        time: timeEST,
      },
    ],
    [timeEST],
  );

  const deliveryEstimateOptions = useMemo(
    () => {
      const nextAvailablePickup = Math.max(1, dayjs(estimatedPickupTs).diff(dayjs(), 'minute'));
      return [
        {
          id: 0,
          title: `Next Available Pickup (${nextAvailablePickup} mins)`,
          time: nextAvailablePickup,
        },
        {
          id: -1,
          title: <CustomEstInput value={timeEST} onChange={setTimeEST} />,
          time: timeEST,
        },
      ];
    },
    [timeEST, estimatedPickupTs],
  );

  const isAcceptingDisabled = () => {
    if (isFoodHubSelfDeliveryOrder) {
      return true;
    }
    if (isOmniPartner(order)) {
      return true;
    }
    /** An estimate must be selected */
    if (isScheduled) {
      return false;
    }
    if (order.has_delivery && !estimatedPickupTs) {
      return true;
    }

    return selectedEstimateOption?.id === -1
      ? !timeEST
      : !selectedEstimateOption?.time;
  };
  const editOrderButton = (
    <Button
      hidden={!isAdmin || !order.is_editable}
      onClick={() => {
        handleEditOrder(orderId);
      }}
    >
      Edit order
    </Button>
  );

  const cancelOrderButton = (
    <Button
      danger
      onClick={() => handleRejectOrder(orderId)}
      loading={rejectLoading}
      hidden={!isAdmin || !order.is_cancellable}
    >
      Cancel order
    </Button>
  );

  const ORDER_DIALOGUE_SETTING = [
    {
      oStatus: [orderStatus.NEW],
      timeSelection: false,
      timeEstSelection: true,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          <Button
            type="primary"
            onClick={() => handleAcceptOrder(orderId)}
            loading={acceptLoading}
            disabled={isAcceptingDisabled()}
            className="ml-0"
          >
            Accept order
          </Button>
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.IN_PROGRESS],
      timeSelection: true,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          {!order.is_catering && (
            <Button
              type="primary"
              onClick={() => handleOrderReady(orderId)}
              loading={readyLoading}
              disabled={isFoodHubSelfDeliveryOrOmniPartner}
              className="ml-0"
            >
              Order ready
            </Button>
          )}
          {(!order?.has_delivery || order?.delivery_type === 'self_delivery') && order?.is_catering && (
            <Button
              type="primary"
              onClick={() => handleOrderDeliver(orderId)}
              loading={deliverLoading}
              className="ml-0"
            >
              Order delivered
            </Button>
          )}
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.READY],
      timeSelection: true,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          {(!order?.has_delivery || order?.delivery_type === 'self_delivery') ? (
            <Button
              type="primary"
              onClick={() => handleOrderDeliver(orderId)}
              loading={deliverLoading}
              className="ml-0"
            >
              Order delivered
            </Button>
          ) : null}
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.ON_THE_WAY],
      timeSelection: true,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton} />
      ),
    },
    {
      oStatus: [orderStatus.COMPLETED],
      timeSelection: false,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton} />
      ),
    },
    {
      oStatus: [orderStatus.DELIVERED],
      timeSelection: false,
      timeEstSelection: false,
      buttons: <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton} />,
    },
    {
      oStatus: [orderStatus.CANCELLED_BY_RESTAURANT],
      timeSelection: false,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} />
      ),
    },
    {
      oStatus: [orderStatus.NEEDS_APPROVAL],
      timeSelection: false,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          <Button
            type="primary"
            onClick={() => handleApproveOrder(orderId)}
            loading={approveLoading}
            disabled={approveLoading}
            className="ml-0"
          >
            {order.has_dispatcher_error
              ? 'Check and Approve'
              : 'Approve order'}
          </Button>
        </ButtonWrapper>
      ),
    },
    {
      oStatus: [orderStatus.PAYMENT_SCHEDULED],
      timeSelection: false,
      timeEstSelection: false,
      buttons: (
        <ButtonWrapper editOrderButton={editOrderButton} cancelOrderButton={cancelOrderButton}>
          <Button
            type="primary"
            onClick={() => handleApproveOrder(orderId)}
            loading={approveLoading}
            disabled={approveLoading}
            className="ml-0"
          >
            {order.has_dispatcher_error
              ? 'Check and Approve'
              : 'Approve order'}
          </Button>
        </ButtonWrapper>
      ),
    },
  ];
  const setting = ORDER_DIALOGUE_SETTING.find((s) => s.oStatus.includes(order.state));

  const checkDispatcher = useCallback(() => {
    if (!orderId) {
      return;
    }
    checkExistDispatcher(orderId)
      .then((result) => {
        setCanRequestDispatcher(result?.status === 200);
      })
      .catch((e) => {
        captureException(e);
        setCanRequestDispatcher(false);
      });
  }, [orderId]);

  useEffect(() => {
    if (!order.id) {
      return;
    }
    if (!order.has_delivery && !selectedEstimateOption) {
      setSelectedEstimateOption(estimateOptions[0]);
    } else {
      checkDispatcher();
    }
  }, [order?.has_delivery, estimateOptions, checkDispatcher, selectedEstimateOption, order?.id]);

  const getOrderDetail = () => {
    if (!order.id) setLoading(true);
    getOrderDetailByOrderId(orderId)
      .then((res) => {
        const detailData = getData(res);
        setOrderDetail(detailData);
        setOrderTime(detailData?.estimate);
      })
      .catch((e) => {
        captureException(e);
        message.error('Something went wrong!');
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (!orderId) {
      setOrderDetail({});
      return;
    }
    getOrderDetail();
  }, [orderId]);

  useEffect(() => {
    if (estimatedPickupTs && order.has_delivery && !selectedEstimateOption) {
      setSelectedEstimateOption(deliveryEstimateOptions[0]);
    }
  }, [estimatedPickupTs, order?.has_delivery, deliveryEstimateOptions, selectedEstimateOption]);

  useEffect(() => {
    if (!orderId) {
      return;
    }
    setEstimatedPickupTs(null);
    if (order.state === 0 && order.has_delivery) {
      isRideServiceAvailable(orderId).then((res) => {
        const error = getData(res)?.order?.error;
        if (!error) {
          estimateOrder(orderId).then((res) => {
            setEstimatedPickupTs(getData(res)?.pickupts);
          });
        } else {
          message.error(error);
        }
      });
    }
  }, [order?.has_delivery, orderId, order.state]);

  const handleAcceptOrder = (id) => {
    if (!isScheduled) {
      /** On demand */
      if (Number(selectedEstimateOption?.time || timeEST) <= 0) return;
      if (order.has_delivery && !selectedEstimateOption?.time && !timeEST) {
        return;
      }

      if (isDeliveryEstimateValid()) {
        setAcceptLoading(true);

        updateOrderState(id, {
          state: 1,
          estimated_time: parseInt(selectedEstimateOption?.time || timeEST, 10),
        })
          .then((res) => {
            setAcceptLoading(false);
            handleOrderUpdate(id, getData(res));
            onClose();
          })
          .catch((err) => {
            captureException(err);
            message.error(getHttpErrorMessage(err));
            setAcceptLoading(false);
          });
      } else {
        message.error('Custom time cannot be less than earliest pickup time');
      }
      return;
    }
    /** Scheduled */
    setAcceptLoading(true);
    updateOrderState(id, {
      state: 1,
    })
      .then((res) => {
        setAcceptLoading(false);
        handleOrderUpdate(id, getData(res));
        onClose();
      })
      .catch((err) => {
        captureException(err);
        message.error(getHttpErrorMessage(err));
        setAcceptLoading(false);
      });

    function isDeliveryEstimateValid() {
      /** If it's delivery type, customEstimate should be less than earliest pickup time*/
      if (
        order?.has_delivery &&
          Number(selectedEstimateOption?.time || timeEST) >=
            dayjs(estimatedPickupTs).diff(dayjs(), 'minute')
      ) {
        /** Delivery type */
        return true;
      }
      if (!order.has_delivery) {
        /** Pickup type */
        return true;
      }
      return false;
    }
  };

  const handleRejectOrder = (id) => {
    setRejectLoading(true);
    updateOrderState(id, {
      state: 2,
    })
      .then((res) => {
        setRejectLoading(false);
        handleOrderUpdate(id, getData(res));
        onClose();
      })
      .catch((err) => {
        captureException(err);
        message.error(getHttpErrorMessage(err));
        setRejectLoading(false);
      });
  };

  const handleApproveOrder = (id) => {
    setApproveLoading(true);
    approveOrder(id)
      .then((res) => {
        handleOrderUpdate(id, getData(res));
        onClose();
      })
      .catch((err) => {
        captureException(err);
        message.error(getHttpErrorMessage(err));
      })
      .finally(() => {
        setApproveLoading(false);
      });
  };

  const handleOrderReady = (id) => {
    setReadyLoading(true);
    updateOrderState(id, {
      state: 4,
    })
      .then((res) => {
        setReadyLoading(false);
        onClose();
      })
      .catch((err) => {
        captureException(err);
        message.error(getHttpErrorMessage(err));
        setReadyLoading(false);
      });
  };

  const isEstimatedTimePassed = (time) => {
    const remainingTime = dayjs(time).diff(dayjs(), 'minute');

    if (remainingTime && remainingTime < 0) {
      return true;
    }
    return false;
  };

  const showEStSelection = !isScheduled && setting?.timeEstSelection;

  const handleUpdateOrder = (id, time) => {
    setTimeConfirmLoading(true);
    updateOrderState(id, {
      estimated_time: parseInt(time, 10),
    })
      .then((res) => {
        setOrderTime(getData(res).estimate);
        handleOrderUpdate(id, getData(res));
        setTimeConfirmLoading(false);
        setShowTimeModal(false);
      })
      .catch((err) => {
        captureException(err);
        message.error(getHttpErrorMessage(err));
        setTimeConfirmLoading(false);
        setShowTimeModal(false);
      });
  };

  const handleOrderDeliver = (id) => {
    setDeliverLoading(true);
    updateOrderState(id, {
      state: orderStatus.DELIVERED,
    })
      .then((res) => {
        setDeliverLoading(false);
        handleOrderUpdate(id, getData(res));
        onClose();
      })
      .catch((err) => {
        captureException(err);
        setDeliverLoading(false);
      });
  };

  const isMigratedData = useMemo(
    () => order.migration_id &&
      order.migration_driver !== 'MealMe' &&
      order.migration_driver !== 'GFOE' &&
      order.migrated_json_order_details?.items?.length,
    [order],
  );

  const orderItems = useMemo(
    () => (isMigratedData && Array.isArray(order.migrated_json_order_details?.items)
      ? order.migrated_json_order_details?.items?.map((item) => ({
        ...item,
        total_value: getItemValue({ value: item.value, migrationDriver: order.migration_driver }),
      }))
      : order.order_items),
    [order, isMigratedData],
  );

  const transformDetailLabel = (label) => {
    const capitalizeLabel = (text) => text.charAt(0).toUpperCase() + text.slice(1);
    return capitalizeLabel(label.split('_').join(' '));
  };

  const findDriver = () => {
    setCanRequestDispatcher(false);
    createDispatcherJob({ id: orderId, organizationId })
      .then((result) => {
        message.open({
          type: 'success',
          content: 'Successfully create a job for dispatcher',
        });
      })
      .catch((e) => {
        captureException(e);
        message.open({
          type: 'error',
          content: 'Something went wrong during request a driver!',
        });
        setCanRequestDispatcher(true);
      });
  };

  const showFindDriverButton = useMemo(
    () => order.state === orderStatus.NEEDS_APPROVAL ||
      order.state === orderStatus.NEW ||
      (order.state === orderStatus.IN_PROGRESS && canRequestDispatcher),
    [order, canRequestDispatcher],
  );

  const handleUpdateOrderState = () => {
    setLoading(true);
    getOrderDetail();
  };

  function renderAlerts() {
    return (
      <>
        {order.has_dispatcher_error && (
          <Alert
            message={`There seems to be an issue with the order. To ensure a resolution, please contact ${WL_CONFIG.name}.`}
            type="error"
            showIcon
            closable
            className="mb-4 mx-4"
          />
        )}
        {order.state === orderStatus.NEEDS_APPROVAL && (
          <Alert
            message="Auto sending has been turned off for this merchant. Send order to merchant so they can view it on their tablet."
            type="warning"
            showIcon
            closable
            className="mb-4 mx-4"
          />
        )}
        {order.has_integration_error && (
          <Alert
            message="Order not processed. Please reorder using the provided link or call the merchant directly.            "
            type="warning"
            showIcon
            closable
            className="mb-4 mx-4"
          />
        )}
      </>
    );
  }

  function renderCustomerInfo() {
    return (
      <Row divider={false} className="flex-col md:flex-row items-start">
        <Space direction="horizontal">
          {order.is_gift
            ? `Sender: ${order.gift_sender_name || order.customer?.name}`
            : order.customer?.name}
          {[PaymentTypes.CASH, PaymentTypes.ALTERNATE_PAYMENT].includes(
            order.payment_type,
          ) && (
            <Typography.Text type="secondary" className="text-xs">
              {order.payment_type === PaymentTypes.CASH
                ? '(Cash Payment)'
                : `(Alt Payment‍‍ ${order.alternate_payment_number})`}
            </Typography.Text>
          )}
        </Space>

        <Space size={isDesktop ? 'large' : 'small'} className="flex-col md:flex-row items-start">
          <JumpLink href={`tel:${order.customer?.phone}`} ellipsis={false}>
            <PhoneOutlined className="mr-1" />
            {order.customer?.phone}
          </JumpLink>
          <JumpLink href={`mailto:${order.customer?.email}`} maxWidth={200}>
            <MailOutlined className="mr-1" />
            {order.customer?.email}
          </JumpLink>
        </Space>

        {order.external_order_details?.tracking_link && (
          <JumpLink
            target="_blank"
            rel="noreferrer noopener"
            href={order.external_order_details?.tracking_link}
          >
            Support
          </JumpLink>
        )}
      </Row>
    );
  }

  function renderRecipientInfo() {
    return (
      order.recipient_number && (
        <Row>
          <Value
            label="Recipient info"
            value={order.recipient_name}
          />
          <JumpLink href={`tel:${order.recipient_number}`}>
            <br />
            <PhoneOutlined className="mr-1" />
            {order.recipient_number}
          </JumpLink>
        </Row>
      )
    );
  }

  function renderAddressInfo() {
    return (
      order.has_delivery && (
        <Row className="flex-col md:flex-row">
          <div className="flex-1">
            <Value
              label="Address"
              value={order.address?.address}
              direction="horizontal"
              className="items-start"
            />
          </div>

          <JumpLink
            target="_blank"
            rel="noreferrer"
            href={`https://google.com/maps?q=${order.address?.location?.lat},${order.address?.location?.lng}`}
            className="justify-self-end"
          >
            Direction
          </JumpLink>
        </Row>
      )
    );
  }

  function renderScheduleInfo() {
    return (
      isScheduled && (
        <Row>
          <Value
            direction="horizontal"
            label={(
              <>
                <CalendarOutlined className="mr-2" />
                Scheduled for
                {' '}
              </>
            )}
          >
            {
              order.is_catering ? (
                <Typography.Text>
                  {dayjs(order.requested_pickup_time || order.scheduled_for).format('ddd MMM DD, LT')}
                </Typography.Text>
              ) : (
                <Typography.Text>
                  {dayjs(order.scheduled_for).format('ddd MMM DD, LT')}
                  -
                  {dayjs(order.scheduled_for).add(0.5, 'hour').format('LT')}
                </Typography.Text>
              )
            }
          </Value>
        </Row>
      )
    );
  }

  function renderDeliveryInfo() {
    return (
      order.has_delivery && (
        <Flex vertical gap={16}>
          <Row>
            <DeliveryTypeSelection
              isScheduled={isScheduled}
              order={order}
              refresh={getOrderDetail}
            />
          </Row>
          <Row className="flex-col md:flex-row gap-4" divider={false}>
            <Value
              label="Delivery status"
              value={
                RIDE_STATUS.find((s) => s.id === order.delivery_state)?.title ||
                '—'
              }
            />

            <Value
              label="Pickup ETA"
              value={
                orderDetail?.estimated_pickup_time
                  ? `${dayjs(orderDetail?.estimated_pickup_time).format(
                    'MMM DD, hh:mm A',
                  )} (${dayjs(orderDetail?.estimated_pickup_time).fromNow()})`
                  : '—'
              }
            />
            <Value label="Driver">
              <Typography.Text>
                {order.driver_name ? order.driver_name : '—'}
                {' '}
                {order.driver_phone && (
                  <a
                    className="driver-phone-link"
                    href={`tel:${order.driver_phone}`}
                  >
                    <span>Call</span>
                  </a>
                )}
              </Typography.Text>
            </Value>

            {order.dispatcher_external_link && (
              <Value label="Dispatch">
                <JumpLink
                  href={order.dispatcher_external_link}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Go to Dispatch
                </JumpLink>
              </Value>
            )}
            {showFindDriverButton && (
              <Button disabled={!canRequestDispatcher} onClick={findDriver}>
                {canRequestDispatcher ? 'Find a driver' : 'Requested'}
              </Button>
            )}
          </Row>
        </Flex>
      )
    );
  }

  function renderEstimateInfo() {
    if (isScheduled || order.state === orderStatus.ON_THE_WAY || !setting?.timeSelection) {
      // If the order is scheduled or it's on the way, we cannot update the time
      return null;
    }
    return (
      <Row>
        <Value
          direction="horizontal"
          label={(
            <>
              {isEstimatedTimePassed(orderTime) && (
                <WarningOutlined style={{ color: 'red' }} className="mr-2" />
              )}
              Estimated time
            </>
          )}
          value={dayjs(orderTime).fromNow()}
        />
        <Button onClick={() => setShowTimeModal(true)}>Update Time</Button>
      </Row>
    );
  }

  function renderItemsInfo() {
    const modifiers = (item) => (item.order_options?.length > 0
      ? item.order_options
      : item.order_modifier);
    return (
      <Row title="Items">
        <List
          className="w-full"
          itemLayout="horizontal"
          size="small"
          dataSource={orderItems || []}
          renderItem={(item, index) => (
            <List.Item>
              <List.Item.Meta
                title={(
                  <div className="flex justify-between">
                    <div>

                      {item.qty && (
                        <>
                          {item.qty}
                          <span className="mx-2">x</span>
                        </>
                      )}
                      {' '}
                      {item.item_name}
                      {' '}
                      {item.size?.trim() && `(${item.size})`}
                      {' '}
                    </div>
                    {formatAmount(item.total_value)}
                  </div>
                )}
                description={(
                  <>
                    {modifiers(item).length > 0 && (
                      <span className="ml-8 block">
                        {(modifiers(item))
                          .map(
                            (m) => `${m.qty > 1 ? m.qty : ''} ${
                              m.modifier_option?.title || m.title
                            } (${m.modifier_option?.price >= 0 ? `$${m.modifier_option?.price}` : m.price})`,
                          )
                          .join(' · ')}

                      </span>
                    )}
                    {item.message && (
                      <Space className="flex ml-8">
                        <MessageOutlined />
                        {item.message}
                      </Space>
                    )}
                    {item.serving_size > 0 && (
                      <Space className="flex ml-8 mt-2" size="small">
                        <InfoCircleOutlined />
                        <span>
                          {renderServingSizeLabel(item.serving_size)}
                        </span>
                      </Space>
                    )}
                  </>

                )}
              />
            </List.Item>
          )}
        />
      </Row>
    );
  }

  function renderNoteInfo() {
    return (
      <>
        {order.admin_note && (
          <Row>
            <Value
              direction="horizontal"
              label="Admin Note"
              value={order.admin_note}
            />
          </Row>
        )}
        {order.delivery_message && (
          <Row>
            <Value
              direction="horizontal"
              label="Customer Note"
              value={order.delivery_message}
            />
          </Row>
        )}
      </>
    );
  }

  function renderFinancialInfo() {
    let fields;
    window.order = order;
    if (!isAdmin) {
      fields = [
        ['Subtotal', formatAmount(order.food_real_value)],
        ['Tax', formatAmount((order.tax_rate || 0) * order.food_real_value)],
        ['Merchant Net', formatAmount(order.restaurant_net)],
        ['Payment Fees', formatAmount(order.order_nets?.restaurant_fee)],
      ];
    } else {
      fields = [
        ['Total', formatAmount(order.value)],
        ['Subtotal', formatAmount(order.food_value)],
        ['Subtotal Without Markup', formatAmount(order.food_real_value)],
        ['Tax', formatAmount(order.order_nets?.total_tax)],
        ['Merchant Tip', formatAmount(order.order_nets?.restaurant_tip_amount)],
        ['Merchant Net', formatAmount(order.restaurant_net)],
        ['Delivery Fee', formatAmount(order.delivery_fare)],
        ['Driver tip', formatAmount(order.order_nets?.rideshare_tip_amount)],
        ['Service Fee', formatAmount(order.service_fee)],
        [
          'Coupon',
          order.coupon_amount != null ? formatAmount(order.coupon_amount) : '-',
        ],
        ['Adjustment Amount', formatAmount(order.adjustment_amount)],
        [
          'Payment Fee',
          formatAmount(order.order_nets?.stripe || order.order_nets?.moneris),
        ],
      ];
    }

    const paymentDetails = order.payment?.custom_order_payment ? (
      <Row>
        <Space direction="vertical">
          <Value
            label={(
              <Space>
                <CreditCardOutlined />
                <Typography.Text style={{ color: 'inherit' }}>Payment</Typography.Text>
              </Space>
            )}
            direction="horizontal"
            value={order.payment.custom_order_payment?.custom_payment?.title || 'Custom'}
          />
          <Space className="pl-5" direction="vertical">
            {
              Object.entries(order.payment.custom_order_payment?.meta ?? {}).map(([key, value]) => (
                <Value
                  label={key}
                  value={value}
                  direction="horizontal"
                />
              ))
            }
          </Space>
        </Space>

      </Row>
    ) : null;
    return (
      <>
        {paymentDetails}
        <Row className="items-center mt-4">
          {renderItems()}
        </Row>
      </>

    );

    function renderItems() {
      return (
        <Descriptions
          bordered
          size="small"
          column={1}
          styles={{ content: { whiteSpace: 'nowrap' } }}
          className="overflow-auto"
        >
          {
            isMigratedData && order.migrated_json_order_details?.details ?
              Object.entries(order.migrated_json_order_details.details)?.map(
                ([key, value]) => (
                  <Descriptions.Item key={key} label={transformDetailLabel(key)}>
                    {value}
                  </Descriptions.Item>
                ),
              ) :
              fields.map(([title, value]) => (
                <Descriptions.Item key={title} label={title}>
                  {value}
                </Descriptions.Item>
              ))

          }
        </Descriptions>
      );
    }
  }

  function renderEstimateForm() {
    const options = order.has_delivery ? deliveryEstimateOptions : estimateOptions;

    return (
      showEStSelection && (
        <Row>
          <Value label="Estimated time">
            {!order.has_delivery || estimatedPickupTs ? (
              <Radio.Group
                value={selectedEstimateOption?.id}
                onChange={({ target: { value } }) => setSelectedEstimateOption(
                  options.find((o) => +o.id === +value),
                )}
              >
                {options.map((option) => (
                  <Radio value={option.id}>{option.title}</Radio>
                ))}
              </Radio.Group>
            ) : (
              <Skeleton paragraph={{ rows: 1 }} />
            )}
            <Typography.Text type="secondary">
              Pick a time that accurately estimates how long this order will
              take to prepare.
            </Typography.Text>
          </Value>
        </Row>
      )
    );
  }

  function renderHeadCount() {
    if (order.headcount) {
      return (
        <Row>
          <Value direction="horizontal" label="Headcount" value={`${order.headcount} people`} />
        </Row>
      );
    }
  }

  function renderMerchantInfo() {
    const {
      has_integration_error: hasIntegrationError,
      integration_merchant_link: merchantLink,
      integration_merchant_phone_number: merchantPhone,
    } = order || {};

    const phone = merchantPhone || order.merchant.phone;
    if (!order.merchant) {
      return null;
    }
    // if (!hasIntegrationError) {
    //   return null;
    // }
    return (
      <>
        <Row>
          <Value label="Merchant name" value={order.merchant?.name} />
          <Space align="end">
            <Value label="Merchant phone number">
              {phone && (
                <JumpLink href={`tel:${phone}`}>
                  <Space>
                    <PhoneOutlined />
                    {phone}
                  </Space>
                </JumpLink>
              )}
            </Value>
            {merchantLink && (<JumpLink href={merchantLink}>Ordering link</JumpLink>)}
          </Space>
        </Row>
        <Row divider={false} className="mt-4">
          <Value label="Merchant type" value={order.merchant?.merchant_type} />
        </Row>
      </>

    );
  }

  function renderContent() {
    if (!order.customer || loading) {
      return <Skeleton paragraph={{ rows: 10 }} className="p-4" />;
    }
    return (
      <div
        className="py-4"
        style={{
          // maxHeight: '70vh',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {renderAlerts()}
        {renderCustomerInfo()}
        {renderRecipientInfo()}
        {renderMerchantInfo()}
        {renderAddressInfo()}
        {renderScheduleInfo()}
        {renderHeadCount()}
        {renderDeliveryInfo()}
        {renderEstimateInfo()}
        {renderItemsInfo()}
        {renderNoteInfo()}
        {renderEstimateForm()}
        {renderFinancialInfo()}
        {showTimeModal && (
          <TimeDialogue
            handleCancel={() => setShowTimeModal(false)}
            handleConfirm={(time) => handleUpdateOrder(orderId, time)}
            loading={timeConfirmLoading}
          />
        )}
        {!disableActions && <SendReceiptSection order={order} />}
        <ChangeLog order={order} />
      </div>
    );
  }

  return (
    <Drawer
      className="order-overlay-drawer"
      title={<Header order={order} onClose={onClose} loading={loading} handleStateChange={handleUpdateOrderState} hasAction={!disableActions} />}
      placement="right"
      onClose={() => {
        setOrderDetail({});
        onClose();
      }}
      closeIcon={false}
      open={open}
      width={720}
      styles={{
        body: { padding: '24px 0' },
        footer: {
          position: 'sticky', bottom: 0, background: '#fff', padding: '10px 16px', textAlign: 'right',
        },
      }}
      footer={!disableActions && (loading ? <Skeleton.Input /> : setting?.buttons ?? null)}
    >
      {renderContent()}
    </Drawer>
  );
}

function Value({
  label, value, children, ...props
}) {
  return (
    <Space direction="vertical" {...props}>
      <Typography.Text type="secondary" style={{ wordBreak: 'normal', textTransform: 'capitalize' }}>{label}</Typography.Text>
      {children || <Typography.Text strong>{value}</Typography.Text>}
    </Space>
  );
}

function CustomEstInput({ value, onChange, disabled = false }) {
  const [errorOpen, setErrorOpen] = useState(false);
  return (
    <Space>
      <Popconfirm
        open={errorOpen}
        title="Invalid Time"
        description="Custom time cannot be more than 60 minutes"
        onConfirm={() => setErrorOpen(false)}
        showCancel={false}
      >
        <Input
          type="number"
          value={value}
          onKeyDown={(e) => {
            ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();
          }}
          onChange={({ target: { value: val } }) => {
            if (val === '' || (val > 0 && val <= 60)) {
              onChange(val);
            } else if (val > 60) {
              onChange(60);
              setErrorOpen(true);
            }
          }}
          disabled={disabled}
          placeholder="Custom time"
        />
      </Popconfirm>
      <span>min</span>
    </Space>
  );
}
function ButtonWrapper({ editOrderButton, cancelOrderButton, children }) {
  return (
    <div className="flex justify-end flex-col sm:flex-row gap-4">
      <div className="flex flex-col sm:flex-row gap-2">
        {editOrderButton || <span />}
        {cancelOrderButton || <span />}
        {children}
      </div>
    </div>
  );
}
OrderOverlay.propTypes = {};

function SendReceiptSection({ order }) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState();
  const { message } = App.useApp();

  function handleSend() {
    setLoading(true);
    http.post(`/v1/orders/${order.id}/send-email/`, { emails: [email] }).then(() => {
      message.success('Email sent');
    }).catch((e) => {
      message.error(getHttpErrorMessage(e));
    }).finally(() => {
      setLoading(false);
    });
  }
  return (
    <Row className="flex-col items-start">
      <Typography.Title level={4}>
        Send order detail via email
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        By entering your email address you can get this order&apos;s details.
      </Typography.Paragraph>
      <Flex gap={16} style={{ maxWidth: 400 }}>
        <Form onFinish={handleSend}>
          <Flex gap={16}>
            <FormItem
              rules={
                [{
                  required: true,
                  message: 'Please enter your email',
                }]
              }
            >
              <Input type="email" value={email} onChange={({ target: { value } }) => { setEmail(value); }} />
            </FormItem>
            <Button htmlType="submit" loading={loading} disabled={loading}>
              Send
            </Button>
          </Flex>
        </Form>

      </Flex>
    </Row>
  );
}

function DeliveryTypeSelection({
  isScheduled,
  order,
  refresh,
}) {
  const [deliveryType, setDeliveryType] = useState(order.delivery_type);
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();
  let disabled = false;
  let alertMessage = '';

  useEffect(() => {
    if (order?.delivery_type) {
      setDeliveryType(order.delivery_type);
    }
  }, [order?.delivery_type]);

  function handleDeliveryTypeChange(deliveryType) {
    setDeliveryType(deliveryType);
    setLoading(true);
    http.post(`/v1/orders/${order.id}/delivery-type`, { delivery_type: deliveryType })
      .then(() => {
        message.success('Delivery type updated');
        refresh();
      })
      .catch((e) => {
        message.error(getHttpErrorMessage(e));
        setDeliveryType(order.delivery_type);
      })
      .finally(() => {
        setLoading(false);
        refresh();
      });
  }

  if (order.state === orderStatus.ON_THE_WAY) {
    disabled = true;
    alertMessage = 'You can no longer change the delivery type because your order is on the way.';
  } else if (isScheduled && dayjs(order.scheduled_for).diff(dayjs(), 'hours') <= 3) {
    disabled = true;
    alertMessage = 'You can no longer change the delivery type as your scheduled delivery is in 3 hours.';
  } else {
    alertMessage = deliveryType === 'self_delivery'
      ? "You selected 'Self-delivery', which means you are responsible for handling the delivery."
      : "You selected 'Omni-dispatch', which means a driver will be assigned from the dispatch service to handle the delivery.";
  }

  return (
    <Flex vertical gap={16}>
      <Value
        direction="horizontal"
        label={
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>Delivery Type</Typography.Text>
        }
        value={(
          <Radio.Group
            value={deliveryType}
            onChange={(e) => handleDeliveryTypeChange(e.target.value)}
            disabled={
              disabled ||
              loading || (
                !order.can_change_delivery_to_omni_dispatch &&
                !order.can_change_delivery_to_self_delivery
              )
            }
            optionType="button"
            size="medium"
            loading={loading}
            className="flex items-center justify-center"
          >
            <Radio.Button
              value="omni_dispatch"
              style={{
                flex: 1,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                fontWeight: 400,
              }}
            >
              <Flex align="center" justify="center" gap={4}>
                {deliveryType === 'omni_dispatch' && <Check />}
                <span>
                  Omni-dispatch
                </span>
              </Flex>

            </Radio.Button>
            <Radio.Button
              value="self_delivery"
              style={{
                flex: 1,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                fontWeight: 400,
              }}

            >
              <Flex align="center" justify="center" gap={4}>
                {deliveryType === 'self_delivery' && <Check />}
                <span>
                  Self-delivery
                </span>
              </Flex>
            </Radio.Button>
          </Radio.Group>
        )}
      />
      <Alert
        message={alertMessage}
        type="info"
        showIcon
      />
    </Flex>
  );
}

export default withGuard(OrderOverlay, [PERMISSIONS.ORDERS], { redirect: true });
